import type {GetServerSideProps, GetServerSidePropsResult} from 'next';
import Head from 'next/head';
import {useRouter} from 'next/router';
import {TITLE_SUFFIX} from '../../../config/constants';
import {GSSKT_HOST} from '../../../config/constants/hosts';
import {CONSULTANT_INDUSTRIES} from '../../../core/entities/company/constants';
import {
  type ResultGetWorkAttractiveConditions,
  getWorkAttractiveConditions,
} from '../../../core/repositories/companyRepository';
import {
  type IndustryCategoriesReturnValueType,
  getIndustryCategories,
} from '../../../core/repositories/reportsRepository';
import {
  type GetUserDataForAnalyticsResponseType,
  getUserDataForAnalytics,
} from '../../../core/repositories/usersMeRepository';
import {validateCookie} from '../../../core/usecases/auth/checkOauthTokenExistence';
import {CompanyListIndustryEngineerPage} from '../../../features/company/components/CompanyListIndustryEngineerPage';
import {CompanyListIndustryPage} from '../../../features/company/components/CompanyListIndustryPage';
import {withHandler} from '../../../server/token/withHandler';
import {ScriptForGA} from '../../../shared/components/ScriptForGA';
import {NoLeftSidebarLayout} from '../../../shared/components/layouts/NoLeftSidebarLayout';
import type {NextPageWithLayout} from '../../_app';

type Props = {
  userDataForAnalytics?: GetUserDataForAnalyticsResponseType;
  metaInfo?: {
    title: string;
    description: string;
    url: string;
  };
  industryIds?: number[];
  industryCategoryId?: number;
  industryCategories?: IndustryCategoriesReturnValueType;
  attractiveConditionsData?: ResultGetWorkAttractiveConditions;
  attractiveConditionQuery?: string[];
};

export const getServerSideProps: GetServerSideProps = withHandler<
  Props,
  'withOptionalSession'
>(
  {auth: 'optional'},
  async (context): Promise<GetServerSidePropsResult<Props>> => {
    const industryCategoriesData = await getIndustryCategories({});
    if (!industryCategoriesData.ok) {
      return {
        props: {},
      };
    }
    const {id, industryIDs} = context.query;
    const industryCategoryId = Number(id) > 0 ? Number(id) : 0;
    const industryIds = industryIDs
      ? industryIDs.toString().split(',').map(Number)
      : [];

    const industryCategoryName =
      industryCategoryId > 1 && industryCategoriesData?.ok
        ? industryCategoriesData.value.industryCategories.find(
            (c) => c.id === industryCategoryId,
          )?.name
        : '';
    const industryName =
      industryCategoryId === 1
        ? CONSULTANT_INDUSTRIES.find((industry) =>
            industry.ids.includes(industryIds[0] ?? 0),
          )?.name
        : '';

    const title =
      industryCategoryId > 1
        ? `${industryCategoryName ?? ''}の企業一覧`
        : industryCategoryId === 1
          ? `${industryName ?? ''}の企業一覧`
          : '企業一覧';
    const url = `${GSSKT_HOST}/company/industry/${industryCategoryId}${industryIds.length > 0 ? `?industryIDs=${industryIds.join(',')}` : ''}`;
    const description =
      'インターン募集、セミナー、説明会、選考会など、厳選した外資・日系トップ企業の新卒採用に関する最新情報を随時掲載';

    const props: Props = {
      metaInfo: {
        title,
        description,
        url,
      },
      industryCategories: industryCategoriesData,
      industryIds,
      industryCategoryId,
      attractiveConditionQuery: [],
    };

    const attractiveConditionsData = await getWorkAttractiveConditions({});
    if (!attractiveConditionsData?.ok) {
      return {
        props,
      };
    }

    const {cookies} = context.req;
    const {query} = context;
    const {isValid} = validateCookie(cookies);
    if (query?.wac) {
      if (Array.isArray(query.wac)) {
        props.attractiveConditionQuery = query.wac as string[];
      } else {
        props.attractiveConditionQuery = [query.wac as string];
      }
    }

    if (!isValid)
      return {
        props: {
          ...props,
          attractiveConditionsData,
        },
      };

    const userDataResponse = await getUserDataForAnalytics({cookies});
    if (!userDataResponse.ok) {
      return {
        props: {
          ...props,
          attractiveConditionsData,
        },
      };
    }

    return {
      props: {
        ...props,
        attractiveConditionsData,
        userDataForAnalytics: userDataResponse.value,
      },
    };
  },
);

const Page: NextPageWithLayout<Props> = ({
  userDataForAnalytics,
  metaInfo,
  industryCategories,
  industryIds,
  industryCategoryId,
  attractiveConditionsData,
  attractiveConditionQuery,
}: Props) => {
  const router = useRouter();
  const {id} = router.query;
  return (
    <>
      {id === 'engineer' ? (
        <CompanyListIndustryEngineerPage
          attractiveConditionsData={attractiveConditionsData}
          attractiveConditionQuery={attractiveConditionQuery}
        />
      ) : (
        <>
          <Head>
            <title key="title">{`${metaInfo?.title}${TITLE_SUFFIX}`}</title>
            <meta
              key="og:title"
              property="og:title"
              content={metaInfo?.title}
            />
            <meta key="og:url" property="og:url" content={metaInfo?.url} />
            <meta name="description" content={metaInfo?.description} />
            <meta
              name="keywords"
              content="外資・日系トップ企業,企業一覧,外資,就活,インターンシップ,新卒採用,会社説明会,就活コラム,ES・体験記,注目企業"
            />
            <link rel="canonical" href={metaInfo?.url} key="canonical" />
            <meta
              name="viewport"
              content="width=device-width,initial-scale=1.0,maximum-scale=1.0"
            />
          </Head>
          <CompanyListIndustryPage
            pageTitle={metaInfo?.title ?? ''}
            industryCategories={industryCategories}
            industryIds={industryIds ?? []}
            industryCategoryId={industryCategoryId ?? 0}
            attractiveConditionsData={attractiveConditionsData}
            attractiveConditionQuery={attractiveConditionQuery}
          />
        </>
      )}
      <ScriptForGA userDataForAnalytics={userDataForAnalytics} />
    </>
  );
};

Page.getLayout = (page) => <NoLeftSidebarLayout>{page}</NoLeftSidebarLayout>;

export default Page;
